'use client';

import { useQuery } from '@tanstack/react-query';
import { endpointsClient } from '@terra/shared-rest-clients-endpoints';
import type { EndpointRelatedWebpageSchema } from '@terra/shared-contracts-endpoints';
import { ENDPOINT_RELATED_WEBPAGES_MAX_LIMIT } from '@terra/shared-contracts-endpoints';
import type { z } from 'zod';

export const ENDPOINT_WEBPAGES_QUERY_KEY = 'endpoint-webpages';

export type EndpointRelatedWebpage = z.infer<
  typeof EndpointRelatedWebpageSchema
>;

type UseEndpointWebpagesParams = {
  endpointId: string | undefined;
  enabled?: boolean;
};

type EndpointWebpagesResult = {
  webpages: EndpointRelatedWebpage[];
  totalCount: number;
};

const fetchEndpointWebpages = async ({
  endpointId,
}: {
  endpointId: string;
}): Promise<EndpointWebpagesResult> => {
  const response = await endpointsClient.getEndpointWebpages({
    params: { id: endpointId },
    query: { skip: 0, limit: ENDPOINT_RELATED_WEBPAGES_MAX_LIMIT },
  });

  if (response.status === 200) {
    return {
      webpages: response.body.webpages,
      totalCount: response.body.totalCount,
    };
  }

  throw new Error('Failed to load endpoint webpages');
};

export const useEndpointWebpages = ({
  endpointId,
  enabled = true,
}: UseEndpointWebpagesParams) => {
  const queryResult = useQuery({
    queryKey: [
      ENDPOINT_WEBPAGES_QUERY_KEY,
      endpointId,
      ENDPOINT_RELATED_WEBPAGES_MAX_LIMIT,
    ],
    queryFn: () => {
      if (!endpointId) {
        return Promise.resolve({ webpages: [], totalCount: 0 });
      }
      return fetchEndpointWebpages({ endpointId });
    },
    enabled: enabled && Boolean(endpointId),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  return {
    webpages: queryResult.data?.webpages ?? [],
    totalCount: queryResult.data?.totalCount ?? 0,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};
